import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { Button, ThemedText, ConfirmDialog } from '../components';
import smsTemplateService from '../api/services/smsTemplateService';
import { extractItems } from '../utils/responseUtils';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../components/TableStyles';
import { ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../components/ModalStyles';
import { CheckCircle, AlertTriangle } from 'lucide-react';

// --- Helper Functions ---

/**
 * Calculate SMS segment count based on encoding.
 * GSM-7: 160 chars single / 153 chars per concat segment
 * UCS-2: 70 chars single / 67 chars per concat segment
 */
const calculateSegmentInfo = (message) => {
  if (!message || message.length === 0) {
    return { charCount: 0, segments: 0, encoding: 'GSM-7', maxSingle: 160 };
  }

  // GSM 03.38 basic character set (simplified check)
  const gsm7Regex = /^[@\u00a3$\u00a5\u00e8\u00e9\u00f9\u00ec\u00f2\u00c7\n\u00d8\u00f8\r\u00c5\u00e5\u0394_\u03a6\u0393\u039b\u03a9\u03a0\u03a8\u03a3\u0398\u039e\u00c6\u00e6\u00df\u00c9 !"#\u00a4%&'()*+,\-.\/0-9:;<=>?\u00a1A-Z\u00c4\u00d6\u00d1\u00dc\u00a7\u00bfa-z\u00e4\u00f6\u00f1\u00fc\u00e0\^{}\\\[~\]|\u20ac]*$/;
  const isGsm7 = gsm7Regex.test(message);

  const charCount = message.length;
  let segments;
  let encoding;
  let maxSingle;

  if (isGsm7) {
    encoding = 'GSM-7';
    maxSingle = 160;
    if (charCount <= 160) {
      segments = 1;
    } else {
      segments = Math.ceil(charCount / 153);
    }
  } else {
    encoding = 'UCS-2';
    maxSingle = 70;
    if (charCount <= 70) {
      segments = 1;
    } else {
      segments = Math.ceil(charCount / 67);
    }
  }

  return { charCount, segments, encoding, maxSingle };
};

/**
 * Extract {{variable}} placeholders from a message body.
 */
const extractVariables = (body) => {
  if (!body) return [];
  const matches = body.match(/\{\{(\w+)\}\}/g);
  if (!matches) return [];
  const unique = [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))];
  return unique;
};

/**
 * Mask a phone number for display: +1***...45
 */
const maskPhone = (phone) => {
  if (!phone) return '-';
  const cleaned = phone.replace(/\s/g, '');
  if (cleaned.length <= 4) return cleaned;
  const first = cleaned.slice(0, 2);
  const last = cleaned.slice(-2);
  return `${first}***...${last}`;
};

/**
 * Format a date string for display.
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
};

/**
 * Compute variable coverage for a template body given registry variables.
 * Returns { used, total, allPresent }
 */
const getVariableCoverage = (body, variables) => {
  if (!variables || variables.length === 0) return { used: 0, total: 0, allPresent: true };
  const present = new Set(extractVariables(body));
  const used = variables.filter(v => present.has(v.name)).length;
  return { used, total: variables.length, allPresent: used === variables.length };
};

// --- Styled Components ---

const TabRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.lg};
  border-bottom: 2px solid ${p => p.theme.colors.border};
`;

const Tab = styled.button`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.lg};
  background: none;
  border: none;
  border-bottom: 2px solid ${p => p.$active ? p.theme.colors.tint : 'transparent'};
  color: ${p => p.$active ? p.theme.colors.tint : p.theme.colors.muted};
  font-weight: ${p => p.$active ? '600' : '400'};
  cursor: pointer;
  margin-bottom: -2px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    color: ${p => p.theme.colors.tint};
  }
`;

const CategorySection = styled.div`
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const CategoryTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  margin-bottom: ${p => p.theme.spacing.md};
  padding-left: ${p => p.theme.spacing.sm};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${p => {
    if (p.$status === 'enabled' || p.$status === 'Sent') return '#22c55e20';
    if (p.$status === 'disabled' || p.$status === 'Failed') return '#ef444420';
    return '#9ca3af20';
  }};
  color: ${p => {
    if (p.$status === 'enabled' || p.$status === 'Sent') return '#22c55e';
    if (p.$status === 'disabled' || p.$status === 'Failed') return '#ef4444';
    return '#9ca3af';
  }};
`;

const ActionButton = styled.button`
  padding: 4px 10px;
  border-radius: ${p => p.theme.borderRadius.sm};
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p =>
    p.$variant === 'primary' ? p.theme.colors.tint :
    p.$variant === 'success' ? (p.theme.colors.success || '#22c55e') :
    p.$variant === 'danger' ? '#ef4444' :
    'transparent'
  };
  color: ${p =>
    (p.$variant === 'primary' || p.$variant === 'success' || p.$variant === 'danger')
      ? 'white'
      : p.theme.colors.text
  };
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  flex-wrap: wrap;
`;

// --- Form Styled Components ---

const FormGroup = styled.div`
  margin-bottom: ${p => p.theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.theme.colors.text};
  margin-bottom: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.tint};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: 14px;
  font-family: 'Courier New', monospace;
  resize: vertical;
  box-sizing: border-box;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.tint};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.tint};
  }
`;

const CharCounter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
  color: ${p => p.$over ? '#ef4444' : p.theme.colors.muted};
  font-family: monospace;
`;

const VariableBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  margin: 2px 4px 2px 0;
  border-radius: 4px;
  font-size: 11px;
  font-family: monospace;
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  color: ${p => p.theme.colors.tint};
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};

  &:hover {
    ${p => p.$clickable ? 'opacity: 0.7;' : ''}
  }
`;

const ConfigBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  border-radius: ${p => p.theme.borderRadius.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
  font-size: 13px;
  background-color: ${p => p.$configured ? '#22c55e10' : '#f59e0b10'};
  border: 1px solid ${p => p.$configured ? '#22c55e40' : '#f59e0b40'};
  color: ${p => p.$configured ? '#22c55e' : '#f59e0b'};
`;

const ConfigIcon = styled.span`
  font-size: 18px;
  flex-shrink: 0;
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${p => p.theme.spacing.md};
  border-top: 1px solid ${p => p.theme.colors.border};
  font-size: 13px;
  color: ${p => p.theme.colors.muted};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl};
  color: ${p => p.theme.colors.muted};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl};
  color: ${p => p.theme.colors.muted};
`;

const EventTypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${p => `${p.theme.colors.tint}20`};
  color: ${p => p.theme.colors.tint};
`;

const OrgBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  margin-left: 6px;
  background-color: ${p => p.$customized ? '#8b5cf620' : '#6b728020'};
  color: ${p => p.$customized ? '#8b5cf6' : '#6b7280'};
`;

const DescriptionText = styled.p`
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
  margin-top: 2px;
  margin-bottom: 0;
`;

const TruncatedText = styled.span`
  display: inline-block;
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  padding: ${p => p.theme.spacing.md};
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const CoverageBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  font-family: monospace;
  background-color: ${p => p.$allPresent ? '#22c55e20' : '#f59e0b20'};
  color: ${p => p.$allPresent ? '#22c55e' : '#f59e0b'};
`;

const VariableCoveragePanel = styled.div`
  margin-top: 12px;
  padding: 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background: ${p => p.theme.colors.backgroundSecondary};
`;

const VariableCoverageItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: ${p => p.theme.colors.border}30;
  }
`;

const VarDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${p => p.$present ? '#22c55e' : '#9ca3af'};
`;

const VarRequiredTag = styled.span`
  font-size: 9px;
  font-weight: 700;
  padding: 1px 4px;
  border-radius: 3px;
  text-transform: uppercase;
  background-color: ${p => p.$required ? '#ef444420' : '#6b728020'};
  color: ${p => p.$required ? '#ef4444' : '#6b7280'};
`;

const VarName = styled.span`
  font-family: monospace;
  font-size: 12px;
  color: ${p => p.theme.colors.tint};
`;

const VarDescription = styled.span`
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// --- Component ---

const SmsTemplatesPage = () => {
  const theme = useTheme();
  const { isSuperAdmin } = useRole();
  const navigate = useNavigate();
  const textareaRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('templates');

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Config state
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);

  // Edit modal state
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ messageBody: '' });
  const [saving, setSaving] = useState(false);
  const [editVariableInfo, setEditVariableInfo] = useState(null);
  const [editVarLoading, setEditVarLoading] = useState(false);

  // Test SMS modal state
  const [testModal, setTestModal] = useState(null);
  const [testForm, setTestForm] = useState({ phoneNumber: '', recipientName: '', params: {} });
  const [sendingTest, setSendingTest] = useState(false);

  // AI generation modal state
  const [aiModal, setAiModal] = useState(null);
  const [aiForm, setAiForm] = useState({ instructions: '' });
  const [generating, setGenerating] = useState(false);
  const [aiVariableInfo, setAiVariableInfo] = useState(null);

  // Variable info cache: { [templateId]: { eventVariables: [...] } }
  const [varInfoCache, setVarInfoCache] = useState({});

  // Revert confirm state
  const [revertConfirm, setRevertConfirm] = useState(null); // template object

  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logPage, setLogPage] = useState(1);
  const [logPageSize] = useState(20);
  const [logTotalPages, setLogTotalPages] = useState(1);
  const [logTotalCount, setLogTotalCount] = useState(0);
  const [logFilterTemplateId, setLogFilterTemplateId] = useState('');

  // --- Load Data ---

  const loadConfig = useCallback(async () => {
    try {
      setConfigLoading(true);
      const response = await smsTemplateService.getConfig();
      const data = response?.data || response;
      setConfig(data);
    } catch {
      setConfig(null);
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await smsTemplateService.getTemplates();
      const items = extractItems(response);
      setTemplates(items);
    } catch {
      toast.error('Failed to load SMS templates');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const params = { page: logPage, pageSize: logPageSize };
      if (logFilterTemplateId) {
        params.templateId = logFilterTemplateId;
      }
      const response = await smsTemplateService.getLogs(params);
      const data = response?.data || response;
      if (data.items) {
        const items = Array.isArray(data.items) ? data.items : data.items?.$values || [];
        setLogs(items);
        setLogTotalPages(data.totalPages || 1);
        setLogTotalCount(data.totalCount || 0);
      } else {
        const items = extractItems(response);
        setLogs(items);
        setLogTotalPages(1);
        setLogTotalCount(items.length);
      }
    } catch {
      toast.error('Failed to load SMS logs');
    } finally {
      setLogsLoading(false);
    }
  }, [logPage, logPageSize, logFilterTemplateId]);

  const loadVariableInfo = useCallback(async (templateId) => {
    if (varInfoCache[templateId]) return varInfoCache[templateId];
    try {
      const response = await smsTemplateService.getVariableInfo(templateId);
      const data = response?.data || response;
      const variables = data.eventVariables || data.EventVariables || [];
      const info = { eventVariables: variables };
      setVarInfoCache(prev => ({ ...prev, [templateId]: info }));
      return info;
    } catch {
      return { eventVariables: [] };
    }
  }, [varInfoCache]);

  // Load variable info for all templates (for table coverage column)
  useEffect(() => {
    if (templates.length === 0) return;
    const loadAll = async () => {
      const promises = templates.map(t => loadVariableInfo(t.id));
      await Promise.all(promises);
    };
    loadAll();
  }, [templates]); // loadVariableInfo omitted intentionally — depends on varInfoCache state

  useEffect(() => {
    loadConfig();
    loadTemplates();
  }, [loadConfig, loadTemplates]);

  useEffect(() => {
    if (activeTab === 'logs') {
      loadLogs();
    }
  }, [activeTab, logPage, logFilterTemplateId, loadLogs]);

  // --- Templates Tab Handlers ---

  const openEditModal = async (template) => {
    setEditModal(template);
    setEditForm({ messageBody: template.messageBody || '' });
    setEditVarLoading(true);
    setEditVariableInfo(null);
    try {
      const info = await loadVariableInfo(template.id);
      setEditVariableInfo(info);
    } catch {
      setEditVariableInfo({ eventVariables: [] });
    } finally {
      setEditVarLoading(false);
    }
  };

  const handleInsertVariable = (varName) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const insertion = `{{${varName}}}`;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const current = editForm.messageBody;
    const newBody = current.substring(0, start) + insertion + current.substring(end);
    setEditForm(prev => ({ ...prev, messageBody: newBody }));
    // Restore cursor after insertion
    setTimeout(() => {
      textarea.focus();
      const newPos = start + insertion.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleEditSave = async () => {
    if (!editModal) return;
    setSaving(true);
    try {
      const payload = {
        messageBody: editForm.messageBody,
      };
      const response = await smsTemplateService.updateTemplate(editModal.id, payload);
      const updated = response?.data || response;
      setTemplates(prev => prev.map(t => t.id === editModal.id ? { ...t, ...updated } : t));
      setEditModal(null);
      toast.success('Template updated');
    } catch {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const openTestModal = (template) => {
    // Use cached variable info if available, else extract from message body
    const cached = varInfoCache[template.id];
    let variables = [];
    if (cached?.eventVariables?.length > 0) {
      variables = cached.eventVariables;
    } else if (template.variables && template.variables.length > 0) {
      variables = template.variables;
    }

    const defaultParams = {};
    if (variables.length > 0) {
      variables.forEach(v => {
        const name = v.name || v;
        defaultParams[name] = v.defaultValue || `test_${name}`;
      });
    } else {
      // Fallback: extract from body
      const bodyVars = extractVariables(template.messageBody);
      const builtinDefaults = {
        organizationName: 'Test Organization',
        formName: 'Test Form',
        submissionId: '12345',
        workOrderNumber: 'WO-001',
        title: 'Test Work Order',
        assignedByName: 'Admin User',
        assignedToName: 'Test User',
        status: 'InProgress',
        reportDownloadLink: 'https://example.com/r/sample-token',
        reportName: 'Test Report',
        reportNumber: 'LR-001',
        labName: 'Test Lab',
        projectName: 'Test Project',
      };
      bodyVars.forEach(v => {
        defaultParams[v] = builtinDefaults[v] || `test_${v}`;
      });
    }

    setTestForm({
      phoneNumber: '',
      recipientName: 'Test User',
      params: defaultParams,
    });
    setTestModal(template);
  };

  const handleSendTest = async () => {
    if (!testModal) return;
    if (!testForm.phoneNumber) {
      toast.error('Phone number is required');
      return;
    }
    setSendingTest(true);
    try {
      const result = await smsTemplateService.sendTestSms(testModal.id, {
        recipientPhone: testForm.phoneNumber,
        recipientName: testForm.recipientName,
        templateParams: Object.keys(testForm.params).length > 0 ? testForm.params : undefined,
      });
      const data = result?.data || result;
      if (data.success !== false) {
        toast.success('Test SMS sent successfully!');
        setTestModal(null);
      } else {
        toast.error(data.message || 'Failed to send test SMS');
      }
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to send test SMS';
      toast.error(message);
    } finally {
      setSendingTest(false);
    }
  };

  // --- Org Override Handlers ---

  const handleCustomize = async (template) => {
    try {
      const result = await smsTemplateService.createOrgOverride(template.id);
      const newTemplate = result?.data || result;
      toast.success('Template customized for your organization');
      await loadTemplates();
      if (newTemplate?.id) {
        openEditModal(newTemplate);
      }
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to customize template';
      toast.error(message);
    }
  };

  const handleRevert = async (template) => {
    setRevertConfirm(null);
    try {
      await smsTemplateService.deleteOrgOverride(template.id);
      toast.success('Reverted to system default');
      await loadTemplates();
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to revert template';
      toast.error(message);
    }
  };

  // --- AI Generation Handlers ---

  const openAiModal = async (template) => {
    setAiModal(template);
    setAiForm({ instructions: '' });
    setAiVariableInfo(null);
    try {
      const info = await loadVariableInfo(template.id);
      setAiVariableInfo(info);
    } catch {
      setAiVariableInfo({ eventVariables: [] });
    }
  };

  const handleAiGenerate = async () => {
    if (!aiModal) return;
    setGenerating(true);
    try {
      const result = await smsTemplateService.generateAiTemplate({
        eventType: aiModal.eventType,
        instructions: aiForm.instructions || undefined,
      });
      const data = result?.data || result;
      if (data.success && data.messageBody) {
        // Apply AI-generated content to the edit form and open it
        setEditModal(aiModal);
        setEditForm({ messageBody: data.messageBody });
        // Load variable info for the edit modal
        setEditVarLoading(true);
        try {
          const info = await loadVariableInfo(aiModal.id);
          setEditVariableInfo(info);
        } catch {
          setEditVariableInfo({ eventVariables: [] });
        } finally {
          setEditVarLoading(false);
        }
        setAiModal(null);
        if (data.warnings?.length > 0) {
          toast.warn(data.warnings.join(' '), { autoClose: 8000 });
        } else {
          toast.success('AI template generated! Review and save.');
        }
      } else {
        toast.error(data.errorMessage || 'AI generation failed');
      }
    } catch (err) {
      const message = err?.response?.data?.message || 'AI generation failed';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  // --- Grouping ---

  const grouped = useMemo(() => {
    return templates.reduce((acc, t) => {
      const cat = t.category || 'System';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(t);
      return acc;
    }, {});
  }, [templates]);

  const categoryOrder = ['WorkOrders', 'Submissions', 'Reports', 'LabReports', 'Tasks', 'System'];
  const sortedCategories = categoryOrder.filter(c => grouped[c]);
  const extraCategories = Object.keys(grouped).filter(c => !categoryOrder.includes(c));
  const allCategories = [...sortedCategories, ...extraCategories];

  // --- Edit Modal Info ---

  const editSegmentInfo = useMemo(() => {
    return calculateSegmentInfo(editForm.messageBody);
  }, [editForm.messageBody]);

  const editPresentVars = useMemo(() => {
    return new Set(extractVariables(editForm.messageBody));
  }, [editForm.messageBody]);

  // --- Render ---

  return (
    <Layout>
      <PageContainer>
        <PageHeader theme={theme}>
          <ThemedText type="title" style={{ fontSize: '24px' }}>
            SMS Templates
          </ThemedText>
        </PageHeader>

        {/* Config Status Banner */}
        {!configLoading && config && (
          <ConfigBanner theme={theme} $configured={config.isConfigured}>
            <ConfigIcon>
              {config.isConfigured ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
            </ConfigIcon>
            <div>
              {config.isConfigured ? (
                <>
                  <strong>SMS service configured</strong>
                  <span style={{ marginLeft: 12, opacity: 0.8 }}>
                    Region: {config.region || 'N/A'}
                    {config.senderId && <> &middot; Sender ID: {config.senderId}</>}
                  </span>
                </>
              ) : (
                <>
                  <strong>SMS service not configured.</strong>
                  <span style={{ marginLeft: 8 }}>
                    See AWS-SNS-SMS-SETUP.md for setup instructions.
                  </span>
                </>
              )}
            </div>
          </ConfigBanner>
        )}

        {/* Tabs */}
        <TabRow theme={theme}>
          <Tab theme={theme} $active={activeTab === 'templates'} onClick={() => setActiveTab('templates')}>
            Templates
          </Tab>
          <Tab theme={theme} $active={activeTab === 'logs'} onClick={() => setActiveTab('logs')}>
            Logs
          </Tab>
        </TabRow>

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <>
            <div style={{
              padding: '10px 16px',
              marginBottom: 16,
              background: theme.colors.surfaceAlt || theme.colors.surface,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: 8,
              fontSize: 13,
              color: theme.colors.textSecondary,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              Notification delivery is now managed in{' '}
              <span
                style={{ color: theme.colors.primary, cursor: 'pointer', fontWeight: 500 }}
                onClick={() => navigate('/notification-events')}
              >
                Notification Events
              </span>
              . This page is for editing template content only.
            </div>
            {loading ? (
              <LoadingState theme={theme}>Loading templates...</LoadingState>
            ) : templates.length === 0 ? (
              <EmptyState theme={theme}>No SMS templates found. Run the seeder to create default templates.</EmptyState>
            ) : (
              allCategories.map(category => (
                <CategorySection key={category} theme={theme}>
                  <CategoryTitle theme={theme}>{category}</CategoryTitle>
                  <TableContainer theme={theme}>
                    <Table>
                      <Thead theme={theme}>
                        <tr>
                          <Th theme={theme}>Name</Th>
                          <Th theme={theme}>Event Type</Th>
                          <Th theme={theme}>Variables</Th>
                          <Th theme={theme}>Chars</Th>
                          <Th theme={theme}>Segments</Th>
                          <Th theme={theme}>Actions</Th>
                        </tr>
                      </Thead>
                      <Tbody>
                        {grouped[category].map(t => {
                          const segInfo = calculateSegmentInfo(t.messageBody);
                          const isOrgOverride = !!t.organizationId;
                          const isSystemTemplate = !t.organizationId && t.isSystemTemplate;
                          const isOrgAdmin = !isSuperAdmin();
                          const cachedVars = varInfoCache[t.id]?.eventVariables;
                          const coverage = cachedVars
                            ? getVariableCoverage(t.messageBody, cachedVars)
                            : null;
                          return (
                            <Tr key={t.id} theme={theme}>
                              <Td theme={theme}>
                                <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                                  {t.name}
                                  {isOrgOverride && <OrgBadge theme={theme} $customized>Customized</OrgBadge>}
                                  {isSystemTemplate && isOrgAdmin && <OrgBadge theme={theme}>System</OrgBadge>}
                                </div>
                                {t.description && (
                                  <DescriptionText theme={theme}>{t.description}</DescriptionText>
                                )}
                              </Td>
                              <Td theme={theme}>
                                <EventTypeBadge theme={theme}>{t.eventType}</EventTypeBadge>
                              </Td>
                              <Td theme={theme}>
                                {coverage ? (
                                  <CoverageBadge theme={theme} $allPresent={coverage.allPresent}>
                                    {coverage.used}/{coverage.total}
                                  </CoverageBadge>
                                ) : (
                                  <span style={{ fontSize: 11, color: theme.colors.muted }}>...</span>
                                )}
                              </Td>
                              <Td theme={theme}>
                                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                  {segInfo.charCount}
                                </span>
                              </Td>
                              <Td theme={theme}>
                                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                  {segInfo.segments}
                                </span>
                              </Td>
                              <Td theme={theme}>
                                <ActionButtons theme={theme}>
                                  {isOrgAdmin && isSystemTemplate && (
                                    <ActionButton theme={theme} $variant="primary" onClick={() => handleCustomize(t)}>
                                      Customize
                                    </ActionButton>
                                  )}
                                  {isOrgOverride && (
                                    <ActionButton theme={theme} $variant="danger" onClick={() => setRevertConfirm(t)}>
                                      Revert
                                    </ActionButton>
                                  )}
                                  {(isSuperAdmin() || isOrgOverride || !isSystemTemplate) && (
                                    <ActionButton theme={theme} onClick={() => openEditModal(t)}>
                                      Edit
                                    </ActionButton>
                                  )}
                                  <ActionButton theme={theme} onClick={() => openAiModal(t)} title="Generate with AI">
                                    AI
                                  </ActionButton>
                                  <ActionButton
                                    theme={theme}
                                    $variant="primary"
                                    disabled={!t.messageBody}
                                    onClick={() => openTestModal(t)}
                                    title={!t.messageBody ? 'Add message body first' : 'Send test SMS'}
                                  >
                                    Test SMS
                                  </ActionButton>
                                </ActionButtons>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CategorySection>
              ))
            )}
          </>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <TableContainer theme={theme}>
            <FilterRow theme={theme}>
              <Label theme={theme} style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Filter by template:</Label>
              <Select
                theme={theme}
                value={logFilterTemplateId}
                onChange={e => { setLogFilterTemplateId(e.target.value); setLogPage(1); }}
                style={{ maxWidth: 300 }}
              >
                <option value="">All templates</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name} ({t.eventType})</option>
                ))}
              </Select>
            </FilterRow>
            {logsLoading ? (
              <LoadingState theme={theme}>Loading logs...</LoadingState>
            ) : logs.length === 0 ? (
              <EmptyState theme={theme}>No SMS logs found.</EmptyState>
            ) : (
              <>
                <Table>
                  <Thead theme={theme}>
                    <tr>
                      <Th theme={theme}>Recipient</Th>
                      <Th theme={theme}>Event Type</Th>
                      <Th theme={theme}>Status</Th>
                      <Th theme={theme}>Segments</Th>
                      <Th theme={theme}>Sent At</Th>
                      <Th theme={theme}>Message</Th>
                    </tr>
                  </Thead>
                  <Tbody>
                    {logs.map(log => (
                      <Tr key={log.id} theme={theme}>
                        <Td theme={theme}>
                          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {maskPhone(log.recipientPhone)}
                          </span>
                        </Td>
                        <Td theme={theme}>
                          <EventTypeBadge theme={theme}>{log.eventType}</EventTypeBadge>
                        </Td>
                        <Td theme={theme}>
                          <StatusBadge $status={log.status}>
                            {log.status}
                          </StatusBadge>
                        </Td>
                        <Td theme={theme}>
                          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {log.segments ?? '-'}
                          </span>
                        </Td>
                        <Td theme={theme} style={{ whiteSpace: 'nowrap' }}>
                          {formatDate(log.sentAt)}
                        </Td>
                        <Td theme={theme}>
                          <TruncatedText title={log.renderedMessage}>
                            {log.renderedMessage || '-'}
                          </TruncatedText>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                <PaginationRow theme={theme}>
                  <span>
                    Showing page {logPage} of {logTotalPages} ({logTotalCount} total)
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <ActionButton
                      theme={theme}
                      disabled={logPage <= 1}
                      onClick={() => setLogPage(p => p - 1)}
                    >
                      Previous
                    </ActionButton>
                    <ActionButton
                      theme={theme}
                      disabled={logPage >= logTotalPages}
                      onClick={() => setLogPage(p => p + 1)}
                    >
                      Next
                    </ActionButton>
                  </div>
                </PaginationRow>
              </>
            )}
          </TableContainer>
        )}

        {/* Edit Modal */}
        <ModalOverlay $isOpen={!!editModal} theme={theme} onClick={() => setEditModal(null)}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()}>
            <ModalHeader theme={theme}>
              <ThemedText type="title" style={{ fontSize: '18px' }}>
                Edit: {editModal?.name}
              </ThemedText>
              <ActionButton theme={theme} onClick={() => setEditModal(null)}>X</ActionButton>
            </ModalHeader>

            <ModalBody theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Message Body</Label>
                <Textarea
                  ref={textareaRef}
                  theme={theme}
                  value={editForm.messageBody}
                  onChange={e => setEditForm(prev => ({ ...prev, messageBody: e.target.value }))}
                  placeholder="Enter SMS message body. Use {{variableName}} for dynamic values."
                />
                <CharCounter
                  theme={theme}
                  $over={editSegmentInfo.charCount > editSegmentInfo.maxSingle && editSegmentInfo.segments > 3}
                >
                  <span>
                    {editSegmentInfo.charCount}/{editSegmentInfo.maxSingle} chars
                  </span>
                  <span>&bull;</span>
                  <span>
                    {editSegmentInfo.segments} segment{editSegmentInfo.segments !== 1 ? 's' : ''}
                  </span>
                  <span>&bull;</span>
                  <span>{editSegmentInfo.encoding}</span>
                </CharCounter>
              </FormGroup>

              {/* Variable Coverage Panel */}
              {editVarLoading ? (
                <DescriptionText theme={theme}>Loading variables...</DescriptionText>
              ) : editVariableInfo?.eventVariables?.length > 0 ? (
                <VariableCoveragePanel theme={theme}>
                  <Label theme={theme} style={{ marginBottom: 8, fontSize: 12 }}>
                    Template Variables
                    <span style={{ fontWeight: 400, marginLeft: 8, color: theme.colors.muted }}>
                      (click to insert at cursor)
                    </span>
                  </Label>
                  {editVariableInfo.eventVariables.map(v => {
                    const isPresent = editPresentVars.has(v.name);
                    return (
                      <VariableCoverageItem
                        key={v.name}
                        theme={theme}
                        onClick={() => handleInsertVariable(v.name)}
                        title={`Click to insert {{${v.name}}}`}
                      >
                        <VarDot $present={isPresent} />
                        <VarName theme={theme}>{'{{' + v.name + '}}'}</VarName>
                        <VarRequiredTag $required={v.required}>
                          {v.required ? 'Required' : 'Optional'}
                        </VarRequiredTag>
                        <VarDescription theme={theme}>{v.description}</VarDescription>
                      </VariableCoverageItem>
                    );
                  })}
                </VariableCoveragePanel>
              ) : null}

              {/* SMS Type as static badge */}
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Label theme={theme} style={{ marginBottom: 0, fontSize: 12 }}>SMS Type:</Label>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 600,
                  backgroundColor: '#3b82f620',
                  color: '#3b82f6',
                }}>
                  Transactional
                </span>
              </div>
            </ModalBody>

            <ModalFooter theme={theme}>
              <Button variant="secondary" size="sm" onClick={() => setEditModal(null)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleEditSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>

        {/* Test SMS Modal */}
        <ModalOverlay $isOpen={!!testModal} theme={theme} onClick={() => setTestModal(null)}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()}>
            <ModalHeader theme={theme}>
              <ThemedText type="title" style={{ fontSize: '18px' }}>
                Send Test SMS: {testModal?.name}
              </ThemedText>
              <ActionButton theme={theme} onClick={() => setTestModal(null)}>X</ActionButton>
            </ModalHeader>

            <ModalBody theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Phone Number *</Label>
                <Input
                  theme={theme}
                  type="tel"
                  value={testForm.phoneNumber}
                  onChange={e => setTestForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  placeholder="+1234567890"
                />
                <DescriptionText theme={theme}>
                  Include country code (e.g., +1 for US/Canada).
                </DescriptionText>
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Recipient Name</Label>
                <Input
                  theme={theme}
                  value={testForm.recipientName}
                  onChange={e => setTestForm(prev => ({ ...prev, recipientName: e.target.value }))}
                  placeholder="Test User"
                />
              </FormGroup>

              {Object.keys(testForm.params).length > 0 && (
                <FormGroup theme={theme}>
                  <Label theme={theme}>Template Variables</Label>
                  <DescriptionText theme={theme} style={{ marginBottom: 8 }}>
                    Edit the values that will be injected into the SMS template.
                  </DescriptionText>
                  {Object.entries(testForm.params).map(([key, value]) => (
                    <div key={key} style={{ marginTop: 6 }}>
                      <Label theme={theme} style={{ fontSize: 11, color: theme.colors.muted, fontFamily: 'monospace' }}>
                        {'{{' + key + '}}'}
                      </Label>
                      <Input
                        theme={theme}
                        value={value}
                        onChange={e => setTestForm(prev => ({
                          ...prev,
                          params: { ...prev.params, [key]: e.target.value },
                        }))}
                      />
                    </div>
                  ))}
                </FormGroup>
              )}
            </ModalBody>

            <ModalFooter theme={theme}>
              <Button variant="secondary" size="sm" onClick={() => setTestModal(null)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSendTest} disabled={sendingTest || !testForm.phoneNumber}>
                {sendingTest ? 'Sending...' : 'Send Test SMS'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>

        {/* AI Generation Modal */}
        <ModalOverlay $isOpen={!!aiModal} theme={theme} onClick={() => setAiModal(null)}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()}>
            <ModalHeader theme={theme}>
              <ThemedText type="title" style={{ fontSize: '18px' }}>
                Generate SMS with AI: {aiModal?.name}
              </ThemedText>
              <ActionButton theme={theme} onClick={() => setAiModal(null)}>X</ActionButton>
            </ModalHeader>

            <ModalBody theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Instructions (optional)</Label>
                <Textarea
                  theme={theme}
                  value={aiForm.instructions}
                  onChange={e => setAiForm(prev => ({ ...prev, instructions: e.target.value }))}
                  placeholder="Optional: describe any specific tone or content preferences..."
                  style={{ minHeight: 80 }}
                />
              </FormGroup>

              {aiVariableInfo?.eventVariables?.length > 0 && (
                <FormGroup theme={theme}>
                  <Label theme={theme}>Available Variables</Label>
                  <DescriptionText theme={theme} style={{ marginBottom: 6 }}>
                    The AI will use these variables in the generated message.
                  </DescriptionText>
                  <div>
                    {aiVariableInfo.eventVariables.map(v => (
                      <VariableBadge key={v.name} theme={theme}>
                        {'{{' + v.name + '}}'}
                        {v.required && (
                          <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.7 }}>*</span>
                        )}
                      </VariableBadge>
                    ))}
                  </div>
                </FormGroup>
              )}
            </ModalBody>

            <ModalFooter theme={theme}>
              <Button variant="secondary" size="sm" onClick={() => setAiModal(null)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleAiGenerate} disabled={generating}>
                {generating ? 'Generating...' : 'Generate'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
        <ConfirmDialog
          isOpen={revertConfirm !== null}
          title="Confirm Revert"
          message="Revert to the system default template? Your customizations will be lost."
          confirmLabel="Revert"
          onConfirm={() => handleRevert(revertConfirm)}
          onCancel={() => setRevertConfirm(null)}
        />
      </PageContainer>
    </Layout>
  );
};

export { SmsTemplatesPage };
